import {getConfigTenant, useUserContext} from "@capone/common";
import {Box, Typography} from "@material-ui/core";
import dayjs from "dayjs";
import {ActionButton, HelpTip} from "halifax";
import React, {useEffect, useRef, useState} from "react";
import {CorpSessionInfo, defaultCorpColors, ModalName, ModalNames} from "redmond";
import {TRAVEL_OFFER_CONFIGS, TravelOfferType, useTravelOffer} from "@capone-corporate/portal";
import {updateUserSeenModal} from "../../../api/v1/user/updateUserSeenModal";
import {
  AVAILABLE,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  getExperimentVariant,
  STAYS_HOMEPAGE,
  useExperiments,
} from "../../../context/experiments";

const formatExpiryDate = (isoDate: string): string => {
  const dateOnly = isoDate.split("T")[0];
  return dayjs(dateOnly).format("MMM D, YYYY");
};

const buildOfferBodyNode = (
  offerType: TravelOfferType,
  firstAmount: string,
  secondAmount: string
): React.ReactNode => {
  const config = TRAVEL_OFFER_CONFIGS.get(offerType);
  if (config == null) return null;

  return config.renderTooltipBody({
    firstAmount,
    secondAmount,
  });
};

export const OfferTooltip = () => {
  const expState = useExperiments();
  const { sessionInfo } = useUserContext(getConfigTenant());
  const travelOffer = useTravelOffer();

  // The config's dismiss key is typed against capone-corporate's ModalName
  // union; cast to redmond's identical union for this repo's wallet APIs.
  const dismissModalName: `${ModalNames}` | ModalNames.FTB_OFFER_TOOLTIP =
    ((travelOffer != null
      ? TRAVEL_OFFER_CONFIGS.get(travelOffer.offerType)?.tooltipDismissModal
      : undefined) as ModalName | undefined) ?? ModalNames.FTB_OFFER_TOOLTIP;

  const isOfferEnabled =
    travelOffer != null &&
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS
    ) === AVAILABLE;

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const hasSeenTooltipBackend =
    sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo?.hasSeenModalMap?.[
          dismissModalName
        ] ?? false
      : true;

  const [showTooltip, setShowTooltip] = useState(false);
  const staysButtonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOfferEnabled || hasSeenTooltipBackend) return;

    // Wait for the funnel tabs to mount before anchoring the tooltip to them.
    const timeoutId = setTimeout(() => {
      const tabClass = staysHomepageEnabled ? "stays" : "hotels";
      const staysTab = document.querySelector(
        `.home-page-header-middle-section-button-wrapper.${tabClass}`
      );
      if (staysTab) {
        staysButtonRef.current = staysTab as HTMLElement;
        setShowTooltip(true);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isOfferEnabled, hasSeenTooltipBackend, staysHomepageEnabled]);

  const handleCloseTooltip = () => {
    setShowTooltip(false);
    updateUserSeenModal({ modalName: dismissModalName }).catch(
      (e: Error) => {
        console.error("Issue persisting seen offer tooltip", e); // eslint-disable-line no-console -- intentional error logging
      }
    );
  };

  if (!isOfferEnabled || travelOffer == null) return null;

  const bodyNode = buildOfferBodyNode(
    travelOffer.offerType,
    travelOffer.firstAmount,
    travelOffer.secondAmount
  );
  const formattedExpiryDate = formatExpiryDate(travelOffer.offerExpiresOn);

  return (
    <HelpTip
      open={showTooltip}
      onClose={handleCloseTooltip}
      anchor={{
        anchorRef: staysButtonRef,
        placement: "bottom",
      }}
      title={
        <Typography
          component="span"
          className="helptip-title"
          style={{ fontSize: "16px", fontWeight: 400, lineHeight: "22px" }}
        >
          {bodyNode}
        </Typography>
      }
      desc={
        <Typography
          component="p"
          className="helptip-desc"
          style={{ fontSize: "12px", opacity: 0.8 }}
        >
          Offer ends on {formattedExpiryDate}.
        </Typography>
      }
      actions={
        <Box display="flex" justifyContent="flex-end" width="100%">
          <ActionButton
            defaultStyle="h4r-primary"
            onClick={handleCloseTooltip}
            message="Got it"
          />
        </Box>
      }
      showCloseButton
      enableClickAway={false}
      className="ftb-tooltip-helptip"
      isNewFeature
      newFeatureLabel="LIMITED TIME OFFER"
      outlineColor="transparent"
      borderRadius="0"
      backgroundColor={defaultCorpColors["blue-12"]}
    />
  );
};
